import { map, Observable } from 'rxjs'
import { TariffService } from '../../core/services/tariff/tariff/tariff.service'

// O searchall-paginated NÃO filtra por origemId/destinoId: o back resolve o trecho a partir de
// cidade/estado (SearchOrigem/SearchDestino) e só então aplica `t.origem_id in (...)`. Mandando
// esses campos vazios a query volta o catálogo inteiro do serviço, paginado por `order by t.id`
// (os mais antigos primeiro), e a verificação nunca encontra o tarifário recém-criado.
// Por isso mandamos cidade/estado. Os ids continuam no payload para o dia em que o back passar a
// respeitá-los; enquanto isso, quem garante a precisão é o filtro exato de temAtivo().
const PAGE_SIZE = 200

interface Trecho {
  origemId: number
  destinoId: number
  origemCidade: string
  origemEstado: string
  destinoCidade: string
  destinoEstado: string
}

// Espelhar o trecho significa inverter também cidade/estado, senão o filtro do back e o filtro
// por id de temAtivo() passam a apontar para sentidos diferentes.
function montarTrecho(model: any, invertido: boolean): Trecho {
  const texto = (v: any) => (v ?? '').toString().trim()
  return {
    origemId: invertido ? model.destinoId : model.origemId,
    destinoId: invertido ? model.origemId : model.destinoId,
    origemCidade: texto(invertido ? model.destinoCidade : model.origemCidade),
    origemEstado: texto(invertido ? model.destinoEstado : model.origemEstado),
    destinoCidade: texto(invertido ? model.origemCidade : model.destinoCidade),
    destinoEstado: texto(invertido ? model.origemEstado : model.destinoEstado),
  }
}

// Monta o filtro de busca de tarifário equivalente para um trecho (origem -> destino).
// A equivalência considera trecho + serviço + veículo/motorista + cliente + perímetro (mesmos
// campos que o back usa em TarifarioEqualSearch, exceto fornecedor/custo/validade).
function montarBusca(model: any, trecho: Trecho) {
  return {
    tipoTarifarioId: model.tipoId ?? 0,
    // Sem 'S' o back não aplica `t.ativo` e a página enche de tarifários inativos, que seriam
    // descartados aqui de qualquer forma — gastando a janela de paginação à toa.
    tarifarioStatus: 'S',
    origemId: trecho.origemId,
    origem: '',
    destinoId: trecho.destinoId,
    destino: '',
    tipoServicoId: model.tipoServicoId ?? 0,
    tipoServico: '',
    servicoId: model.servicoId ?? 0,
    servico: '',
    tipoVeiculoId: model.tipoVeiculoId ?? 0,
    tipoVeiculo: '',
    tipoMotoristaId: model.tipoMotoristaId ?? 0,
    tipoMotorista: '',
    clienteId: model.clienteId ?? 0,
    cliente: '',
    fornecedorId: 0,
    fornecedor: '',
    standard: 'N',
    perimetroUrbano: model.perimetroUrbano ?? '',
    // Descrição é deliberadamente vazia: o back usa `like '%...%'` e um ponto específico
    // ("Aeroporto X") excluiria os demais pontos da mesma cidade, que também são equivalentes.
    origemDescricao: '',
    origemCidade: trecho.origemCidade,
    origemEstado: trecho.origemEstado,
    destinoDescricao: '',
    destinoCidade: trecho.destinoCidade,
    destinoEstado: trecho.destinoEstado,
  }
}

function temAtivo(resp: any, origemId: number, destinoId: number): boolean {
  const lista = resp?.data?.tarifarios ?? []
  return lista.some(
    (t: any) =>
      t?.ativo?.toLowerCase?.() === 's' &&
      t.origemId === origemId &&
      t.destinoId === destinoId,
  )
}

// Verifica, sem gravar, se já existe tarifário equivalente ATIVO para um sentido:
// - invertido = false: o próprio trecho sendo criado (origem -> destino);
// - invertido = true: o trecho espelhado (destino -> origem).
// Reaproveita o searchall-paginated existente.
export function existeTarifarioEquivalente(
  tariffService: TariffService,
  model: any,
  invertido: boolean,
): Observable<boolean> {
  const trecho = montarTrecho(model, invertido)
  return tariffService
    .searchAllPagedTariff(1, PAGE_SIZE, montarBusca(model, trecho))
    .pipe(map((resp) => temAtivo(resp, trecho.origemId, trecho.destinoId)))
}
